import {
    Typography,
    Box,
    TextField,
    List,
    ListItem,
    ListItemIcon,
    Avatar,
    InputAdornment,
    IconButton,
    Tooltip,
    LinearProgress,
    Paper,
    Stack,
    Chip,
    alpha,
    Button
} from '@mui/material';
import {
    Send as SendIcon,
    SmartToy as BotIcon,
    Person as PersonIcon,
    Chat as ChatIcon,
    AutoAwesome as SparkleIcon,
    DeleteSweep as ClearIcon
} from '@mui/icons-material';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { useTheme } from '@emotion/react';

import CustomMarkDown from '../../../components/custom-markdown/CustomMarkDown';
import Iconify from '../../../components/Iconify';
import { actions } from '../../../helper/ActionText';
import ApiUrls from '../../../helper/ApiUrls';
import useResponsive from '../../../hooks/useResponsive';
import { API } from '../../../services/axios.service';
import { socket } from '../../../services/socket.service';
import { getUniqueSocketBroadcastID } from '../../../helper';

const languages = {
    "en": "English",
    "bn": "Bangla"
};

const PromptBox = ({ historyDel, onClearConversation }) => {
    const { i18n } = useTranslation();
    const theme = useTheme();
    const params = useParams();
    const mdUp = useResponsive('up', 'md');
    const [topicInfo,setTopicInfo] = useState(null);
    const botInfo = useSelector((state) => state.selectedAgent.data);

    const { enqueueSnackbar } = useSnackbar();
    const [message, setMessage] = useState('');
    const [sendingMessage, setSendingMessage] = useState(false);
    const [reply, setReply] = useState('');
    const [isConnected, setIsConnected] = useState(socket.connected);
    const [agentAction, setAgentAction] = useState(null);
    const [conversations, setConversations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchConversations = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await API.get(`topics/chatbot/${params.chatbotId}/playground/messages`);
                setConversations(response.data.data);
            } catch (err) {
                setError(err.response ? err.response.data : { error: 'An unknown error occurred' });
            } finally {
                setLoading(false);
            }
        };

        fetchConversations();
    }, [params.chatbotId, historyDel]);


    // Socket useEffect
    useEffect(() => {
        const socketBroadcastID = getUniqueSocketBroadcastID();
        function onConnect() {
            console.log('***** sock connected *******');
            setIsConnected(true);
        }

        function onDisconnect() {
            console.log('***** sock disconnected *******');
            setIsConnected(false);
        }

        function onMessageEvent(value) {
            console.log('socket', value);
            const { data: { message }, action } = value;
            const currentAction = actions[action];

            if (currentAction.systemText === actions.final_answer.systemText) {
                setReply((previous) => `${previous}${message.content}`);
                setAgentAction(null);
            } else {
                setAgentAction(currentAction);
            }
        }

        socket.on('connect', onConnect);
        socket.on('disconnect', onDisconnect);
        socket.on(`agent/playground/${socketBroadcastID}`, onMessageEvent);

        return () => {
            socket.off('connect', onConnect);
            socket.off('disconnect', onDisconnect);
            socket.off(`agent/playground/${socketBroadcastID}`, onMessageEvent);
        };
    }, [botInfo?.id]);

    useEffect(() => {
        if (conversations.length > 0) {
            const lastMessageListItem = document.getElementById(`convo_${conversations.length - 1}`);
            lastMessageListItem?.scrollIntoView({ behavior: "smooth", block: "end", inline: "nearest" });
        }
    }, [conversations]);

    const handleMessageInputKeyDown = (e) => {
        if (e.key === 'Enter' && e.shiftKey) {
            e.preventDefault();
            setMessage((prev) => `${prev}\n`);
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (message.trim().length === 0) {
                setMessage((prev) => `${prev}\n`);
            } else {
                sendMessage();
            }
        }
    };

    useEffect(() => {
        if (sendingMessage) {
            sendMessageRESTCall();
        }
    }, [sendingMessage]);
    

    const sendMessageRESTCall = async () => {
        const payload = {
            message: conversations[conversations.length - 1].content,
            language: languages[i18n.language]
        };
        if (topicInfo) {
            payload.topic_id = topicInfo.id;
        }
        payload.from_playground = true;
        const socketBroadcastID = getUniqueSocketBroadcastID();
        payload.socket_broadcast_id = socketBroadcastID;

        API.post(ApiUrls.SEND_MESSAGE_ALPHA(botInfo?.id), payload)
            .catch(err => {
                console.log('chat call rest err', err);
                enqueueSnackbar('Failed to send message. Please try again.', { variant: 'error' });
            })
            .finally(() => {
                setSendingMessage(false);
            });
    };
    
    useEffect(() => {
        const textToShow = agentAction?.displayText || reply;
        if (String(textToShow).length > 0) {
            if (conversations.length > 0 && conversations[conversations.length - 1].type === 'ai') {
                const lastConvo = conversations[conversations.length - 1];
                lastConvo.content = textToShow;
                setConversations([...conversations.slice(0, -1), lastConvo]);
            } else {
                setConversations([...conversations, { content: textToShow, type: 'ai' }]);
            }
        }
    }, [reply, agentAction]);

    const sendMessage = async (customMessage = '') => {
        const contentToSend = customMessage || message;
        if (String(contentToSend).trim().length > 0) {
            setReply('');
            setSendingMessage(true);
            setConversations([...conversations, { content: contentToSend, type: 'human' }]);
            if (!customMessage) {
                setMessage('');
            }
        }
    };

    // Helper functions to avoid nested ternaries
    const getAvatarForMessage = (item) => {
        if (item.type === 'ai' && botInfo?.persona_photo) {
            return (
                <Avatar
                    src={botInfo?.persona_photo}
                    alt={botInfo?.persona_name}
                    sx={{ width: 36, height: 36 }}
                />
            );
        }

        if (item.type === 'human') {
            return <PersonIcon fontSize="small" />;
        }

        return <BotIcon fontSize="small" />;
    };

    const getUserName = (item) => item.type === 'human' ? 'You' : botInfo?.name;

    const getChipColor = (item) => item.type === 'human' ? 'primary' : 'secondary';

    const getChipLabel = (item) => item.type === 'human' ? 'User' : 'AI';

    const getAvatarBgColor = (item) => item.type === 'human' ? 'primary.main' : 'secondary.main';

    const getBubbleStyles = (item) => {
        const isHuman = item.type === 'human';
        return {
            p: 2,
            bgcolor: isHuman
                ? theme.palette.primary.main
                : alpha(theme.palette.grey[500], 0.1),
            color: isHuman ? 'white' : 'text.primary',
            borderRadius: 2,
            borderTopLeftRadius: isHuman ? 2 : 0.5,
            borderTopRightRadius: isHuman ? 0.5 : 2,
            wordBreak: 'break-word',
            maxWidth: '85%'
        };
    };

    const getEmptyStateAvatar = () => {
        if (botInfo?.persona_photo) {
            return (
                <Avatar
                    src={botInfo.persona_photo}
                    alt={botInfo.persona_name}
                    sx={{ width: 80, height: 80 }}
                />
            );
        }
        return <BotIcon sx={{ fontSize: 64, color: 'primary.main' }} />;
    };

    const getChatHeaderAvatar = () => {
        if (botInfo?.persona_photo) {
            return (
                <Avatar
                    src={botInfo.persona_photo}
                    alt={botInfo.persona_name}
                    sx={{ width: 40, height: 40 }}
                />
            );
        }
        return <ChatIcon />;
    };

    const getConnectionStatus = () => ({
        color: isConnected ? 'success.main' : 'error.main',
        text: isConnected ? 'Online' : 'Offline'
    });

    const getInputPlaceholder = () => {
        if (botInfo && mdUp) {
            return `Ask ${botInfo.name} anything...`;
        }
        return `Message ${botInfo?.name}`;
    };

    const getSendIcon = () => {
        if (sendingMessage) {
            return <Iconify icon="svg-spinners:8-dots-rotate" />;
        }
        return <SendIcon fontSize="small" />;
    };

    const isSendDisabled = () => sendingMessage || message.trim().length === 0 || message.trim().length > 65000;

    // Empty state component
    const EmptyState = () => (
        <Box sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            p: 4,
            textAlign: 'center'
        }}>
            <Box sx={{
                width: 120,
                height: 120,
                borderRadius: 3,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 3
            }}>
                {getEmptyStateAvatar()}
            </Box>

            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                Start Testing Your Agent
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 400 }}>
                Send a message below or try one of these sample prompts to see how your agent responds
            </Typography>

            {/* Sample Prompts */}
            {botInfo?.sample_prompts && botInfo?.sample_prompts.length > 0 && (
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                    gap: 2,
                    maxWidth: 600,
                    width: '100%'
                }}>
                    {botInfo.sample_prompts.map((prompt, i) => (
                        <Paper
                            key={i}
                            elevation={0}
                            onClick={() => sendMessage(prompt)}
                            sx={{
                                p: 2,
                                cursor: 'pointer',
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 2,
                                transition: 'all 0.2s ease-in-out',
                                '&:hover': {
                                    borderColor: 'primary.main',
                                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                                    transform: 'translateY(-2px)',
                                    boxShadow: theme.shadows[4]
                                }
                            }}
                        >
                            <Stack direction="row" alignItems="flex-start" spacing={1}>
                                <SparkleIcon
                                    sx={{
                                        fontSize: 16,
                                        color: 'primary.main',
                                        mt: 0.25,
                                        flexShrink: 0
                                    }}
                                />
                                <Typography
                                    variant="body2"
                                    sx={{
                                        lineHeight: 1.4,
                                        color: 'text.primary'
                                    }}
                                >
                                    {prompt}
                                </Typography>
                            </Stack>
                        </Paper>
                    ))}
                </Box>
            )}
        </Box>
    );

    // Message bubble component
    const MessageBubble = ({ item, index }) => (
        <ListItem
            id={`convo_${index}`}
            sx={{
                p: 1,
                alignItems: 'flex-start',
                '&:hover': {
                    bgcolor: alpha(theme.palette.action.hover, 0.5)
                }
            }}
        >
            <ListItemIcon sx={{
                alignSelf: 'flex-start',
                minWidth: 48,
                mt: 0.5
            }}>
                <Tooltip title={getUserName(item)} arrow placement="left">
                    <Avatar sx={{
                        width: 36,
                        height: 36,
                        bgcolor: getAvatarBgColor(item)
                    }}>
                        {getAvatarForMessage(item)}
                    </Avatar>
                </Tooltip>
            </ListItemIcon>

            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                        {getUserName(item)}
                    </Typography>
                    <Chip
                        label={getChipLabel(item)}
                        size="small"
                        color={getChipColor(item)}
                        sx={{ height: 18, fontSize: '0.7rem' }}
                    />
                    {item.created_at && (
                        <Typography variant="caption" color="text.secondary">
                            {moment(item.created_at).format('HH:mm')}
                        </Typography>
                    )}
                </Stack>

                <Paper
                    elevation={0}
                    sx={getBubbleStyles(item)}
                >
                    {agentAction && index === conversations.length - 1 && item.type === 'ai' && (
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                            <Iconify icon={agentAction.icon} sx={{ width: 16, height: 16 }} />
                            <Typography variant="caption" sx={{ fontStyle: 'italic', opacity: 0.8 }}>
                                {agentAction.displayText}
                            </Typography>
                        </Stack>
                    )}

                    {item.type === 'human' ? (
                        <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                            {item.content}
                        </Typography>
                    ) : (
                        <CustomMarkDown content={item.content} />
                    )}
                </Paper>
            </Box>
        </ListItem>
    );

    // Error state component
    const ErrorState = () => (
        <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="error" variant="h6" sx={{ mb: 1 }}>
                Oops! Something went wrong
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                We couldn't load the conversation. Please try refreshing the page.
            </Typography>
            <Button
                variant="outlined"
                onClick={() => window.location.reload()}
                size="small"
            >
                Refresh Page
            </Button>
        </Box>
    );

    // Loading state component
    const LoadingState = () => (
        <Box sx={{ p: 2 }}>
            <LinearProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                Loading conversation history...
            </Typography>
        </Box>
    );

    // Main chat content renderer
    const renderChatContent = () => {
        if (error) {
            return <ErrorState />;
        }

        if (loading) {
            return <LoadingState />;
        }

        if (conversations.length > 0) {
            return (
                <List sx={{ p: 1 }}>
                    {conversations.map((item, idx) => (
                        <MessageBubble
                            key={idx}
                            item={item}
                            index={idx}
                        />
                    ))}
                </List>
            );
        }

        return <EmptyState />;
    };

    return (
        <Box sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column'
        }}>
            {/* Chat Header */}
            <Paper
                elevation={0}
                sx={{
                    p: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    flexShrink: 0
                }}
            >
                <Stack direction="row" alignItems="center" spacing={2}>
                    <Avatar sx={{
                        bgcolor: 'primary.main',
                        width: 40,
                        height: 40
                    }}>
                        {getChatHeaderAvatar()}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {botInfo?.persona_name}
                        </Typography>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Box sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: getConnectionStatus().color
                            }} />
                            <Typography variant="caption" color="text.secondary">
                                {getConnectionStatus().text}
                            </Typography>
                        </Stack>
                    </Box>
                    {sendingMessage && (
                        <Chip
                            label="Typing..."
                            size="small"
                            color="primary"
                            variant="outlined"
                            icon={<Iconify icon="svg-spinners:3-dots-bounce" />}
                        />
                    )}
                    <Tooltip
                        title="Clear conversation history"
                        arrow
                    >
                        <IconButton
                            size="small"
                            onClick={onClearConversation}
                            sx={{
                                bgcolor: alpha('#f44336', 0.1),
                                color: 'error.main',
                                '&:hover': {
                                    bgcolor: 'error.main',
                                    color: 'white'
                                }
                            }}
                        >
                            <ClearIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Paper>

            {/* Chat Content */}
            <Box sx={{
                flex: 1,
                overflowY: 'auto',
                minHeight: 0,
                '&::-webkit-scrollbar': {
                    width: 6
                },
                '&::-webkit-scrollbar-track': {
                    background: alpha('#000', 0.05)
                },
                '&::-webkit-scrollbar-thumb': {
                    background: alpha('#000', 0.2),
                    borderRadius: 3
                }
            }}>
                {renderChatContent()}
            </Box>

            {/* Message Input */}
            <Box sx={{
                p: 2,
                borderTop: '1px solid',
                borderColor: 'divider',
                flexShrink: 0
            }}>
                <TextField
                    disabled={sendingMessage}
                    autoComplete="off"
                    size="small"
                    placeholder={getInputPlaceholder()}
                    value={message}
                    multiline
                    minRows={1}
                    maxRows={4}
                    onChange={(e) => setMessage(e.target.value)}
                    fullWidth
                    error={message.trim().length > 65000}
                    helperText={message.trim().length > 65000 ? 'Message is too long' : ''}
                    onKeyDown={handleMessageInputKeyDown}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 3
                        }
                    }}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    disabled={isSendDisabled()}
                                    onClick={() => sendMessage()}
                                    size="small"
                                    sx={{
                                        bgcolor: 'primary.main',
                                        color: 'white',
                                        '&:hover': {
                                            bgcolor: 'primary.dark'
                                        },
                                        '&:disabled': {
                                            bgcolor: alpha('#000', 0.1),
                                            color: alpha('#000', 0.3)
                                        }
                                    }}
                                >
                                    {getSendIcon()}
                                </IconButton>
                            </InputAdornment>
                        )
                    }}
                />
            </Box>
        </Box>
    );
};

export default PromptBox;